import {Flex, Tooltip} from 'antd';
import {ModelDetailDTO, ModelDTO} from 'hemwb-api';
import {FileOutlined, HeartFilled, HeartOutlined} from '@ant-design/icons';
import {getGlobalAdaptation, sortColumn} from './utils';
import {ModelStatusTag, tagType} from './ModelStatusTag';
import {TO_LOCALE_DATE_STRING_OPTIONS} from '../../../core/TableWithSortAndPagination';
import {ActionsCell, OperationCell} from './ModelItemActionMenu';
import {GlobalAdaptationCell} from './GlobalAdaptationCell';
import {useToggleFavorite} from '../../../../store/favorite';

export const useGetModelListColumns = (isExternalUser: boolean) => {
  const toggleFavorite = useToggleFavorite();

  const favoriteColumn = {
    title: ' ',
    key: 'favorite',
    width: '3%',
    render: (model: ModelDTO & {favorite?: boolean}) => (
      <Tooltip title={model.favorite ? 'Remove from favorites' : 'Add to favorites'}>
        <span
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(model.uuid, !!model.favorite);
          }}
          style={{cursor: 'pointer', fontSize: '16px', color: model.favorite ? '#00857c' : '#bfbfbf'}}
        >
          {model.favorite ? <HeartFilled /> : <HeartOutlined />}
        </span>
      </Tooltip>
    ),
  };

  return [
    ...(isExternalUser ? [] : [favoriteColumn]),
    {
      title: 'Model Name',
      key: 'name',
      width: '40%',
      render: (model: ModelDTO & {isChild?: boolean; children?: ModelDTO[]; level?: number}) => {
        const hasChildren = !!model?.children?.length;
        const level = model?.level || 0;

        if (model?.isChild) {
          // Child rows - progressive indent based on level
          // If has children: no extra indent (expand icon provides spacing)
          // If leaf: indent to align after where arrow would be (level * 48 + 16)
          const indentAmount = hasChildren ? 0 : level * 48 + 16;

          // Adaptation children with file icon
          if (model.metadata.global !== 'true') {
            return (
              <Flex style={{marginLeft: `${indentAmount}px`}}>
                <FileOutlined style={{marginRight: '16px'}} />
                <Flex>{model?.name}</Flex>
              </Flex>
            );
          }
          // Global children
          return (
            <Flex style={{marginLeft: `${indentAmount}px`}}>
              <Flex>{model?.name}</Flex>
            </Flex>
          );
        }

        // Root/parent rows - add spacing if has children (for expand icon)
        const parentSpacing = hasChildren ? 16 : 0;
        return (
          <Flex style={{marginLeft: `${parentSpacing}px`}}>
            <Flex>{model?.name}</Flex>
          </Flex>
        );
      },
      sorter: (a: ModelDTO, b: ModelDTO) => sortColumn(a, b, 'name'),
    },
    {
      title: 'Model Type',
      dataIndex: 'modelType',
      key: 'modelType',
      width: '10%',
      sorter: (a: ModelDTO, b: ModelDTO) => sortColumn(a.metadata, b.metadata, 'model_type'),
    },
    {
      title: 'Global / Adaptation',
      key: 'globalAdaptation',
      width: '11%',
      render: (model: ModelDTO) => <GlobalAdaptationCell model={model} />,
      sorter: (a: ModelDTO, b: ModelDTO) => {
        const aVal = getGlobalAdaptation(a);
        const bVal = getGlobalAdaptation(b);
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        return aVal.localeCompare(bVal);
      },
    },
    {
      title: 'Latest Version',
      key: 'latestVersion',
      width: '10%',
      render: (model: ModelDTO) =>
        model.metadata.version_release_of_model ? <span>Ver. {model.metadata.version_release_of_model}</span> : '-',
      sorter: (a: ModelDTO, b: ModelDTO) => sortColumn(a.metadata, b.metadata, 'version_release_of_model'),
    },
    {
      title: 'Latest modified',
      key: 'modifiedOn',
      width: '11%',
      render: (model: ModelDTO & {modifiedOn: string}) =>
        model.modifiedOn ? new Date(model.modifiedOn).toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS) : '-',
      sorter: (a: ModelDTO & {modifiedOn: string}, b: ModelDTO & {modifiedOn: string}) => {
        const aDate = a.modifiedOn ? new Date(a.modifiedOn) : null;
        const bDate = b.modifiedOn ? new Date(b.modifiedOn) : null;
        if (aDate === null && bDate === null) return 0;
        if (aDate === null) return 1;
        if (bDate === null) return -1;
        return aDate.getTime() - bDate.getTime();
      },
      defaultSortOrder: 'descend',
    },
    {
      title: 'Status',
      key: 'status',
      width: '5%',
      render: (model: ModelDTO) => <ModelStatusTag status={model?.metadata?.status?.toLowerCase() as tagType} />,
      sorter: (a: ModelDTO, b: ModelDTO) => sortColumn(a.metadata, b.metadata, 'status'),
    },
    {
      title: 'HEDS Lead',
      key: 'steward',
      width: '13%',
      render: (model: ModelDTO) =>
        model.metadata.name_of_model_steward ? <span>{model.metadata.name_of_model_steward}</span> : '-',
      sorter: (a: ModelDTO, b: ModelDTO) => {
        const aVal = a.metadata.name_of_model_steward;
        const bVal = b.metadata.name_of_model_steward;
        if (!aVal && !bVal) return 0;
        if (!aVal) return 1;
        if (!bVal) return -1;
        return aVal.localeCompare(bVal);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (model: ModelDetailDTO) => <ActionsCell model={model} />,
    },
    {
      title: ' ',
      key: 'operation',
      render: (model: ModelDTO) => <OperationCell model={model} tourId="model-row-action" />,
    },
  ];
};
